import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/ui/nav-link";
import { Menu, X, Phone, ChevronDown, Linkedin, User, LogIn } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User as SupabaseUser } from "@supabase/supabase-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hideOnScroll, setHideOnScroll] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Auth state listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => {
            checkAdminStatus(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto-hide header while reading blog content (improves mobile reading space).
  useEffect(() => {
    const isBlogRoute = location.pathname.startsWith("/blog");
    if (!isBlogRoute) {
      setHideOnScroll(false);
      return;
    }

    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;

        // Hide on downward scroll once user has started reading.
        if (y > 80 && delta > 5) setHideOnScroll(true);
        // Show when scrolling up (or near top).
        if (delta < -5 || y < 40) setHideOnScroll(false);

        lastY = y;
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data } = await supabase.rpc('is_admin', { user_id: userId });
      setIsAdmin(data || false);
    } catch (error) {
      console.error("Error checking admin status:", error);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Signed out successfully",
    });
    navigate("/");
  };

  const copyPhoneNumber = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText('+447857450763');
    toast({
      title: "Phone number copied!",
      description: "+447857450763 is in your clipboard. Shall we start your growth journey?",
      duration: 5000,
    });
  };

  const solutionsItems = [
    { label: "Demand Generation", path: "/demand-generation" },
    { label: "Account-Based Marketing", path: "/abm" },
    { label: "Pipeline Acceleration", path: "/pipeline-acceleration" },
    { label: "LinkedIn SDR", path: "/linkedin-sdr" },
    { label: "Website Design", path: "/website-design" },
  ];

  const navItems: Array<{ label: string; path: string; external?: boolean }> = [
    { label: "Home", path: "/" },
    { label: "Paid Ads", path: "/paid-ads" },
    { label: "About", path: "/about" },
    { label: "Pricing", path: "/pricing" },
  ];

  return (
    <header
      className={
        "fixed top-0 left-0 right-0 z-50 bg-background border-b border-border shadow-sm transition-transform duration-300 will-change-transform " +
        (hideOnScroll ? "-translate-y-full" : "translate-y-0")
      }
    >
      <div className="container mx-auto px-2 sm:px-4">
        <div className="flex items-center justify-between h-12">
          {/* Brand Logo & Text */}
          <Link to="/" className="flex items-center space-x-1 hover:opacity-80 transition-opacity flex-shrink-0">
            <img 
              src="/lovable-uploads/24311fcc-e22a-4067-8009-f30f40492885.png" 
              alt="VGP Logo" 
              className="h-5 w-5 object-contain flex-shrink-0"
            />
            <span className="font-bold text-xs text-foreground hover:text-secondary transition-colors whitespace-nowrap">
              Vincent Growth Partners
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {/* LinkedIn Connect */}
            <a
              href="https://www.linkedin.com/in/vincentgrowthpartners"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-1 text-xs font-medium text-[#0A66C2] hover:text-[#094c93] transition-colors"
            >
              <Linkedin className="w-3.5 h-3.5" />
              <span>Connect</span>
            </a>
            
            {/* Divider */}
            <div className="h-4 w-px bg-border/50" />
            
            {/* Phone Number */}
            <div 
              onClick={copyPhoneNumber}
              className="flex items-center space-x-1.5 text-xs font-medium text-foreground hover:text-primary transition-colors group cursor-pointer flex-shrink-0"
              title="Click to copy number"
            >
              <div className="w-5 h-5 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Phone className="w-2.5 h-2.5 text-white" />
              </div>
              <span className="font-semibold whitespace-nowrap">07857 450763</span>
            </div>
            
            <nav className="flex items-center gap-3">
              {/* Home link */}
              <NavLink to="/">
                Home
              </NavLink>

              {/* Solutions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="group relative text-xs font-medium transition-all duration-300 whitespace-nowrap text-muted-foreground hover:text-primary flex items-center gap-1 outline-none">
                  <span className="relative z-10 px-1.5 py-0.5 rounded-md transition-all duration-300 group-hover:bg-primary/10">
                    Solutions
                  </span>
                  <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-background border border-border shadow-lg z-50 min-w-[200px]">
                  {solutionsItems.map((item) => (
                    <DropdownMenuItem key={item.label} asChild>
                      <Link 
                        to={item.path}
                        className="cursor-pointer text-sm hover:bg-muted"
                      >
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Other nav items */}
              {navItems.slice(1).map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  external={item.external}
                >
                  {item.label}
                </NavLink>
              ))}
              <Button variant="premium" size="sm" className="relative overflow-hidden group transition-transform duration-300 text-xs px-2 py-1 h-7" asChild>
                <Link to="/#lead-form">
                  <span className="relative z-10 whitespace-nowrap">Book a Strategy Call</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-primary to-primary-light opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
                </Link>
              </Button>
              
              {/* Auth/User Button */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                      <User className="w-3.5 h-3.5 mr-1" />
                      <span className="max-w-[80px] truncate">{user.email?.split('@')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background border border-border shadow-lg z-50 min-w-[160px]">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer text-sm">
                        My Dashboard
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="cursor-pointer text-sm">
                            Admin Panel
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/leads" className="cursor-pointer text-sm">
                            Leads Dashboard
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-sm text-destructive">
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" asChild>
                  <Link to="/auth">
                    <LogIn className="w-3.5 h-3.5 mr-1" />
                    Login
                  </Link>
                </Button>
              )}
            </nav>
          </div>

          {/* Tablet LinkedIn + Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            {/* LinkedIn icon for tablet */}
            <a
              href="https://www.linkedin.com/in/vincentgrowthpartners"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center justify-center w-8 h-8 bg-[#0A66C2] rounded-full hover:bg-[#094c93] transition-colors"
              aria-label="Connect on LinkedIn"
            >
              <Linkedin className="w-4 h-4 text-white" />
            </a>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            {/* 50-50 Contact Banner */}
            <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-border">
              {/* Call Section */}
              <div 
                onClick={copyPhoneNumber}
                className="flex flex-col items-center justify-center p-3 bg-primary/5 rounded-lg cursor-pointer hover:bg-primary/10 transition-colors"
                title="Click to copy number"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mb-2">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-sm text-foreground">07857 450763</span>
                <p className="text-xs text-muted-foreground mt-1 text-center">AI Reception Available 24/7</p>
              </div>
              
              {/* LinkedIn Section */}
              <a
                href="https://www.linkedin.com/in/vincentgrowthpartners"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center p-3 bg-[#0A66C2]/5 rounded-lg hover:bg-[#0A66C2]/10 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="w-10 h-10 bg-[#0A66C2] rounded-full flex items-center justify-center mb-2">
                  <Linkedin className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-sm text-[#0A66C2]">Connect on LinkedIn</span>
                <p className="text-xs text-muted-foreground mt-1 text-center">Avg response: 15 minutes</p>
              </a>
            </div>
            
            <nav className="flex flex-col space-y-2.5">
              <NavLink 
                to="/" 
                variant="mobile"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </NavLink>
              
              {/* Solutions Section */}
              <div className="space-y-1.5">
                <span className="text-sm font-semibold text-foreground">Solutions</span>
                <div className="pl-3 space-y-1.5 border-l-2 border-primary/20">
                  {solutionsItems.map((item) => (
                    <NavLink
                      key={item.label}
                      to={item.path}
                      variant="mobile"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>

              {navItems.slice(1).map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  variant="mobile"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
              
              <Button variant="premium" size="lg" className="w-full mt-3 h-12 text-base font-bold" asChild>
                <Link to="/#lead-form" onClick={() => setIsMenuOpen(false)}>
                  Book a Strategy Call
                </Link>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;